import React, { useState, useEffect, useMemo, SyntheticEvent } from "react";

import {
  Button,
  Dropdown,
  DropdownProps,
  Form,
  FormField,
  FormGroup,
  Modal,
} from "semantic-ui-react";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import { PickedState } from "react-lib/apps/MEH/sequence/AppointmentListMEH";
import { BTN_ACTS } from "react-lib/apps/MEH/types/MeBillingType";
import SearchBoxWithKey from "react-lib/appcon/common/SearchBoxWithKey";

import PatientAppointmentSerializer from "issara-sdk/types/PatientAppointmentSerializer_apps_QUE";
import DoctorSerializer from "issara-sdk/types/DoctorSerializer_core";

type ModRegisterServiceFromAppointmentProps = Pick<
  PickedState,
  "buttonLoadCheck" | "searchedItemListWithKey"
> & {
  setProp: (key: any, value: any, callback?: () => void) => any;
  onEvent: (e: any) => void;
  open: boolean;
  data?: PatientAppointmentSerializer | null;
  onClose?: () => void;
  onRegister?: (providerName: string) => void;
  // options
  doctorList?: DoctorSerializer[];
};

const FIELD_STYLE: React.CSSProperties = {
  color: "#333",
  fontSize: "15px",
  padding: "2px 0",
};

const LABEL_STYLE: React.CSSProperties = {
  fontWeight: 600,
  color: "#333",
  fontSize: "15px",
};

const ModRegisterServiceFromAppointment = (props: ModRegisterServiceFromAppointmentProps) => {
  const [selectedProvider, setSelectedProvider] = useState<Record<string, any> | null>(null);

  useEffect(() => {
    if (props.open) {
      setSelectedProvider({
        provider_id: props.data?.display_info?.provider,
        full_name: props.data?.display_info?.provider_name,
      });
    } else {
      setSelectedProvider(null);
    }
  }, [props.data, props.open]);

  const fields = [
    {
      label: "ชื่อผู้ใช้บริการ",
      value: `${props.data?.patient_pre_name || ""} ${props.data?.patient_first_name || ""} ${
        props.data?.patient_last_name || ""
      }`,
      type: "text",
    },
    {
      label: "ชื่อบัญชีทางการ",
      value: props.data?.division_name,
      type: "text",
    },
    {
      label: "Package ที่รับบริการ",
      value: (
        <>
          {props.data?.package_orders?.map((pk: any, index: number) => (
            <div
              key={pk.id}
              style={{
                display: "flex",
                gridTemplateColumns: "6.5rem auto",
                ...((props.data?.package_orders || []).length > 1 && { marginTop: "10px" }),
              }}
            >
              <div
                style={{
                  color: pk.package_type_name === "Package" ? "#5D8439" : "#FE9500",
                  minWidth: "max-content",
                }}
              >
                {pk.package_type_name} :
              </div>
              <div>{pk.product_name}</div>
            </div>
          ))}
        </>
      ),
      type: "text",
    },
    {
      label: "ผู้ให้บริการ",
      value: props.data?.doctor_full_name,
      type: "dropdown",
      required: true,
    },
  ];

  const mapProviderOptions = (items: any[]) => {
    const doctorCodes = new Set((props.doctorList || []).map((d) => d.code));

    return items
      .filter((item) => doctorCodes.has(item.code))
      .map((item) => ({
        key: item.provider_id,
        text: `${item?.pre_name || ""} ${item.first_name} ${item.last_name}`,
        value: item.provider_id,
      }));
  };

  return (
    <Modal open={props.open} style={{ width: "50rem" }} closeOnDimmerClick onClose={props.onClose}>
      <div style={{ padding: "28px 32px 24px 32px" }}>
        {/* Title */}
        <div
          style={{
            color: "var(--primary-theme-color)",
            fontSize: "18px",
            fontWeight: 700,
            marginBottom: "3rem",
          }}
        >
          ลงทะเบียนรับบริการจากนัดหมาย
        </div>

        <Form>
          {fields.map((field, idx) => (
            <FormGroup
              key={idx}
              style={{
                margin: "0 0 2rem 0",
                alignItems: "center",
                ...(field.label === "Package ที่รับบริการ" && { alignItems: "baseline" }),
              }}
            >
              <FormField width={4}>
                <label style={LABEL_STYLE}>
                  {field.label}
                  {field.required && (
                    <span style={{ color: "var(--primary-theme-color)", marginLeft: "2px" }}>
                      *
                    </span>
                  )}
                </label>
              </FormField>
              <FormField width={8}>
                {field.type === "dropdown" ? (
                  <SearchBoxWithKey
                    onEvent={props.onEvent}
                    type="Provider"
                    id="RSFA"
                    placeholder="เลือกผู้ให้บริการ"
                    searchedItemListWithKey={props.searchedItemListWithKey}
                    selectedItem={selectedProvider?.provider_id}
                    valueKey="provider_id"
                    setSelected={(value, key, data) => {
                      setSelectedProvider(data);
                    }}
                    mapOptions={mapProviderOptions}
                    defaultText={selectedProvider?.full_name || ""}
                  />
                ) : (
                  <div style={FIELD_STYLE}>{field.value || "-"}</div>
                )}
              </FormField>
            </FormGroup>
          ))}
        </Form>

        {/* Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "16px",
            marginTop: "4rem",
          }}
        >
          <Button
            style={{
              background: "#bbb",
              color: "#fff",
              minWidth: "120px",
            }}
            onClick={props.onClose}
          >
            ปิดหน้าต่าง
          </Button>

          <ButtonLoadCheck
            setProp={props.setProp}
            onClick={() => {
              props.onRegister?.(
                `${selectedProvider?.first_name || ""} ${selectedProvider?.last_name || ""}`.trim(),
              );
            }}
            paramKey={`${BTN_ACTS.CREATE_VISIT_APPOINTMENT}_${props.data?.id}`}
            buttonLoadCheck={
              props.buttonLoadCheck?.[`${BTN_ACTS.CREATE_VISIT_APPOINTMENT}_${props.data?.id}`]
            }
            disabled={!selectedProvider?.provider_id}
            size="small"
            style={{
              backgroundColor: "var(--primary-theme-color)",
              color: "white",
              paddingLeft: "0.35rem",
              paddingRight: "0.35rem",
            }}
            title="ลงทะเบียนรับบริการ"
          />
        </div>
      </div>
    </Modal>
  );
};

ModRegisterServiceFromAppointment.displayName = "ModRegisterServiceFromAppointment";
export default ModRegisterServiceFromAppointment;
